/**
 * Display helpers — kept tiny and pure so they're trivially testable later.
 * No date libraries; we use the built-in `Intl` formatters.
 */

const SHORT_DATETIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return SHORT_DATETIME.format(d);
}

/**
 * Returns a human relative time ("3 min ago", "2 days ago"). Returns
 * "never" for null. The Intl API always picks a single unit; we walk a
 * small cascade to land on one that reads naturally.
 */
export function formatRelative(iso: string | null): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const cascade: Array<[number, Intl.RelativeTimeFormatUnit]> = [
    [60, "second"],
    [60, "minute"],
    [24, "hour"],
    [7, "day"],
    [4.34524, "week"],
    [12, "month"],
    [Number.POSITIVE_INFINITY, "year"],
  ];
  let value = diffSec;
  let unit: Intl.RelativeTimeFormatUnit = "second";
  for (const [div, nextUnit] of cascade) {
    if (Math.abs(value) < div) break;
    value = value / div;
    unit = nextUnit;
  }
  return RELATIVE.format(-Math.round(value), unit);
}

/** Color-code last_seen per the brief: green <10m, yellow <1h, red older/null. */
export type FreshnessTone = "green" | "yellow" | "red";

export function freshnessOf(iso: string | null): FreshnessTone {
  if (!iso) return "red";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "red";
  const diffMs = Date.now() - d.getTime();
  if (diffMs < 10 * 60 * 1000) return "green";
  if (diffMs < 60 * 60 * 1000) return "yellow";
  return "red";
}

export function freshnessClasses(tone: FreshnessTone): string {
  switch (tone) {
    case "green":
      return "bg-emerald-500/15 text-emerald-300 ring-1 ring-inset ring-emerald-500/30";
    case "yellow":
      return "bg-amber-500/15 text-amber-300 ring-1 ring-inset ring-amber-500/30";
    case "red":
      return "bg-rose-500/15 text-rose-300 ring-1 ring-inset ring-rose-500/30";
  }
}

export function formatUptimeSecs(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s) || s < 0) return "—";
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m`;
  return `${Math.floor(s)}s`;
}

/**
 * Human file size — IEC base-1024 ("MiB") because tarballs are typically a
 * few hundred KiB to a couple MiB and the SI vs IEC distinction matters
 * less than picking ONE consistent unit family.
 */
export function formatBytes(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n < 0) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KiB", "MiB", "GiB", "TiB"] as const;
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[i]}`;
}

/** Short sha — first 12 chars is plenty to disambiguate within a release set. */
export function shortSha(sha: string | null | undefined, n = 12): string {
  if (!sha) return "—";
  return sha.slice(0, n);
}
